import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import {
  PageContextFacade,
  runtimeSelectors,
  UrlParametersFacade,
} from '@blaze-runtime/engine-data-access';
import { CanvasElementId, CanvasElementType, getNamespaceId } from '@blaze-runtime/engine-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { RenderElementDataRowContext, RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { loggerCreate } from '@blaze-shared/logger-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';

import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { ElementHierarchyService } from '../../../../services/element-hierarchy.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import { hasDataProps } from '../../../element-model/element-props-guards';
import { isGeneratedForm } from '../../../element-model/element-type-guards';
import { getDataSourceInputName } from '../../../element-model/io';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';
import { INPUT_DATA_STREAM } from '../input-data-stream.token';

import { DataSourceProvider } from './data-source.data-provider';

const log = loggerCreate('visible_eval', 'info', 'ExpressionDataProvider', null);

interface ElementInputsContext {
  workflowOutput?: any;
  inputValue?: any;
}

@Injectable({ providedIn: 'root' })
export class ExpressionDataProvider implements DataSourceProvider {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementHierarchyService = inject(ElementHierarchyService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly injector = inject(Injector);
  private readonly intelliSense = inject(IntelliSenseService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly pageParametersFacade = inject(UrlParametersFacade);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject(Store);
  private readonly variablesFacade = inject(VariablesFacade);

  private readonly varValues$: Observable<Record<string, any>> = toObservable(
    this.variablesFacade.varValues,
  );

  readonly sourceType = 'expression' as const;

  getDataStream({
    source,
    element,
    inputName,
    rowContext,
  }: DataProviderQuery): Observable<InputDataPackage> {
    const expression = source.expression;
    return this.getExpressionRuntimeContext(element?.id, element?.type, rowContext).pipe(
      map((context) => {
        const result =
          inputName === 'opened'
            ? this.intelliSense.queryExpressionObj(
                'open' as unknown as any,
                expression as unknown as any,
                context,
              )
            : (this.intelliSense.evalExpressionObj(expression, context, true) as unknown);

        return { result, context };
      }),
      distinctUntilChanged((x, y) => x.result === y.result),
      map((payload) => {
        const result: InputDataPackage = {
          sourceType: 'expression',
          data: payload.result,
          schema: undefined,
        };
        if (inputName === 'visible') {
          log('getDataStream', (tag) =>
            console.log(`${tag} [visible] ${element?.id} | ${element?.type} → ${payload.result}`, {
              expression,
              context: payload.context,
            }),
          );
        }
        return result;
      }),
    );
  }

  getExpressionRuntimeContext(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType | undefined,
    rowContext?: RenderElementDataRowContext,
  ): Observable<RuntimeContext> {
    const defaultOutputEventType =
      this.elementDefinitionsService.getDefaultOutputEventType(elementType);

    return combineLatest({
      $$Account: this.projectContextFacade.varAccount$.pipe(
        map((varAccount) => (varAccount?.Name ? varAccount : {})),
      ),
      $$Page:
        elementId !== undefined
          ? this.pageContextFacade
              .getPageContextForPage(getNamespaceId(elementId))
              .pipe(map((pageContext) => pageContext?.data ?? {}))
          : of({}),
      $$User: this.projectContextFacade.varUser$.pipe(map((varAccount) => varAccount ?? {})),
      $$Url: this.pageParametersFacade
        .getPageVariables$(elementId ? getNamespaceId(elementId) : undefined)
        .pipe(
          map((params) =>
            params.reduce(
              (result, p) => {
                result[p.name] = p.value;
                return result;
              },
              {} as Record<string, string>,
            ),
          ),
        ),
      storedVars: this.varValues$.pipe(startWith({})),
      elementInputs:
        elementId !== undefined
          ? combineLatest({
              // The input workflow data as `$$input1`
              workflowOutput: this.store.select(runtimeSelectors.selectConnections).pipe(
                // Find connection from a workflow to the element
                map((connections) =>
                  connections.find(
                    (conn) =>
                      conn.target.layerType === 'Ui' &&
                      conn.target.id === elementId &&
                      conn.source.layerType === 'Workflow',
                  ),
                ),
                switchMap((connection) =>
                  connection !== undefined
                    ? this.store.select(
                        runtimeSelectors.selectWorkflowOutputsById(connection.source.id),
                      )
                    : of(undefined),
                ),
              ),
              inputValue: this.elementStoreView.getElement(elementId).pipe(
                switchMap((element) => {
                  if (!hasDataProps(element)) {
                    return of(undefined);
                  }

                  const data = element.props.data.find(
                    (d) => d.key === getDataSourceInputName(element.type),
                  );
                  if (!data?.dataSource || data.dataSource.sourceType === 'expression') {
                    // If data source missing or the source is an expression (infinite loop to self) - the inputValue is undefined
                    return of(undefined);
                  }

                  if (isGeneratedForm(element) && defaultOutputEventType) {
                    return this.store
                      .select(
                        runtimeSelectors.selectOutputUiDataById(
                          element?.id,
                          defaultOutputEventType,
                        ),
                      )
                      .pipe(
                        map((v) => ({
                          length: v ? 1 : 0,
                        })),
                      );
                  }

                  return this.injector
                    .get(INPUT_DATA_STREAM)
                    .getInputDataStream({
                      inputName: data.key,
                      source: data.dataSource,
                      bindings: data.bindings,
                      element,
                      binding: data,
                      expression: true,
                      rowContext,
                    })
                    .pipe(
                      map((v) => ({
                        length: v.data?.length || 0,
                      })),
                      startWith({ length: 0 }),
                    );
                }),
              ),

              // Element's output data as `$$output`
              output: defaultOutputEventType
                ? this.store.select(
                    runtimeSelectors.selectOutputUiDataById(elementId, defaultOutputEventType),
                  )
                : of(undefined),

              // Parent details view
              details: this.getParentContext(
                elementId,
                'formDetails',
                elementType === 'formDetails',
              ),
              // Parent form view
              form: this.getParentContext(
                elementId,
                'formContainer',
                elementType === 'formContainer',
              ),
              // Parent form list item view
              formList: this.getParentContext(
                elementId,
                'editableList',
                elementType === 'editableList',
              ).pipe(
                map((data: any) =>
                  Array.isArray(data) && rowContext?.rowIndex !== undefined
                    ? data[rowContext.rowIndex]
                    : data,
                ),
              ),
            })
          : of({} as ElementInputsContext),

      // `$$[named inputs of input workflow]`
      namedInputs: this.getNamedInputContext(elementId),
      formDetailsNamedInputs: this.getParentNamedInputsContext(elementId, 'formDetails'),
      formContainerNamedInputs: this.getParentNamedInputsContext(elementId, 'formContainer'),
      wizardStepVisibility: this.getWizardStepVisibility(elementId),
    }).pipe(
      map(
        ({
          $$Account,
          $$Page,
          $$User,
          $$Url,
          elementInputs,
          storedVars,
          namedInputs,
          formDetailsNamedInputs,
          formContainerNamedInputs,
          wizardStepVisibility,
        }) =>
          ({
            ...elementInputs,
            // For backwards compatibility; this `input1` is often overwritten by `input1` in namedInputs below which is a different thing
            // Which is why separate `workflowOutput` field is now present to avoid confusion and unclear behavior.
            input1: elementInputs.workflowOutput,
            ...namedInputs,
            ...formDetailsNamedInputs,
            ...formContainerNamedInputs,
            ...(wizardStepVisibility ? { wizardStepVisible: wizardStepVisibility } : {}),
            $$Account,
            $$Page,
            $$User,
            $$Url,
            storedVars,
          }) as RuntimeContext,
      ),
    );
  }

  private getParentContext(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType,
    currentCheck = false,
  ): Observable<any> {
    if (!elementId) {
      return of({});
    }

    return this.elementHierarchyService
      .getParentElement(elementId, elementType, currentCheck)
      .pipe(
        switchMap((parentElement) =>
          parentElement !== undefined
            ? this.store.select(
                runtimeSelectors.selectOutputUiDataById(
                  parentElement.id,
                  this.elementDefinitionsService.getDefaultOutputEventType(parentElement.type),
                ),
              )
            : of(undefined),
        ),
      );
  }

  private getNamedInputContext(
    elementId: string | undefined,
  ): Observable<{ [x: string]: any } | undefined> {
    const workflowWithConnection$ = this.store.select(runtimeSelectors.selectConnections).pipe(
      switchMap((connections) => {
        const connection = connections.find(
          (conn) =>
            conn.target.layerType === 'Ui' &&
            conn.target.id === elementId &&
            conn.source.layerType === 'Workflow',
        );
        if (!connection) {
          return of(undefined);
        }
        return this.store
          .select(runtimeSelectors.selectWorkflowById(connection.source.id))
          .pipe(map((workflow) => ({ workflow, connections })));
      }),
    );

    return workflowWithConnection$.pipe(
      switchMap((payload) => {
        if (!payload) {
          return of(undefined);
        }

        const { workflow, connections } = payload;
        if (!workflow) {
          return of(undefined);
        }

        const namedInputs$ = Object.entries(workflow.inputs).reduce(
          (acc, [inputName, connectionId]) => {
            const inputConnection = connections.find(({ id }) => id === connectionId);
            if (!inputConnection || inputConnection.source.layerType !== 'Ui') {
              return acc;
            }

            acc[inputName] = this.store.select(
              runtimeSelectors.selectOutputUiDataById(
                inputConnection.source.id,
                inputConnection.eventType,
              ),
            );
            return acc;
          },
          {} as Record<string, Observable<any>>,
        );

        return Object.keys(namedInputs$).length ? combineLatest(namedInputs$) : of(undefined);
      }),
    );
  }

  private getParentNamedInputsContext(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType,
  ): Observable<{ [x: string]: any } | undefined> {
    return this.elementHierarchyService
      .getParentElement(elementId, elementType)
      .pipe(switchMap((element) => this.getNamedInputContext(element?.id)));
  }

  private getWizardStepVisibility(
    elementId: string | undefined,
  ): Observable<Record<string, any> | undefined> {
    return this.elementHierarchyService.findParentWizard(elementId).pipe(
      switchMap((wizard) => {
        return wizard
          ? this.store.select(runtimeSelectors.selectElementMetaDataFeaturesById(wizard.id)).pipe(
              map((elementMeta) =>
                elementMeta && elementMeta.elementType === 'wizard'
                  ? elementMeta.hiddenSteps
                  : undefined,
              ),
              map((hiddenSteps) =>
                Object.fromEntries(
                  wizard?.props.content.steps?.items.map((step, index) => [
                    step.id,
                    !hiddenSteps?.includes(index),
                  ]) ?? [],
                ),
              ),
            )
          : of(undefined);
      }),
    );
  }
}
